'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';

import { Button } from '../shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shadcn/dropdown-menu';
import { Trans } from './trans';

function MobileNavigationDropdown({
  links,
  currentPath,
}: {
  links: {
    path: string;
    label: string;
  }[];
  /**
   * Optional current path override. When omitted the dropdown reads
   * `usePathname()`. The active link (and trigger label) is chosen by
   * longest-prefix match so a subroute (`.../applications/edit`) still
   * highlights its parent nav item (`.../applications`).
   */
  currentPath?: string;
}) {
  const pathname = usePathname();
  const path = currentPath ?? pathname;

  const currentPathName = useMemo(() => {
    // Longest-prefix match: prefer an exact hit, otherwise the most specific
    // link whose path is a prefix of the current route. Sorting by path
    // length descending makes the first match the most specific.
    const sorted = [...links].sort((a, b) => b.path.length - a.path.length);
    const match =
      sorted.find((link) => link.path === path) ??
      sorted.find((link) => path.startsWith(`${link.path}/`));
    return match?.label;
  }, [links, path]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={'secondary'} className={'w-full'}>
          <span
            className={'flex w-full items-center justify-between space-x-2'}
          >
            <span>
              <Trans i18nKey={currentPathName} defaults={currentPathName} />
            </span>

            <ChevronDown className={'h-5'} />
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={
          'dark:divide-dark-700 w-screen divide-y divide-gray-100' +
          ' rounded-none'
        }
      >
        {Object.values(links).map((link) => {
          return (
            <DropdownMenuItem asChild key={link.path}>
              <Link
                className={'flex h-12 w-full items-center'}
                href={link.path}
              >
                <Trans i18nKey={link.label} defaults={link.label} />
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default MobileNavigationDropdown;
